import { loadScript } from '@mews-framework/utils';
import { RecaptchaAction } from './recaptchaAction';
import { ContextWithRecaptcha } from './types';

interface InitOptions {
    /**
     * You are allowed to hide the badge only if you include the reCAPTCHA branding visibly in the user flow.
     * More details: https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed.
     *
     * We haven't made it a default to make it more clear that you need to include the reCAPTCHA branding.
     */
    shouldHideBadge: boolean;
}

export interface RecaptchaService {
    initialize: (options: InitOptions) => Promise<void>;
    execute: (action: RecaptchaAction) => Promise<string | null>;
}

export const createRecaptchaService = (
    apiToken: string | null | undefined,
    context: Window = window
): RecaptchaService => {
    const contextWithRecaptcha = context as ContextWithRecaptcha;

    /** Initialization can throw. */
    const initialize = async ({ shouldHideBadge }: InitOptions) => {
        if (apiToken == null) return;

        if (isRecaptchaServiceInitialized(contextWithRecaptcha)) {
            return Promise.resolve();
        }

        if (shouldHideBadge) {
            hideBadge();
        }

        /**
         * We use www.recaptcha.net instead of google.com to avoid sending
         * other cookies set by Google.
         *
         * https://developers.google.com/recaptcha/docs/faq#does-recaptcha-use-cookies
         */
        const recaptchaUrl = `https://www.recaptcha.net/recaptcha/enterprise.js?render=${apiToken}`;
        await loadScript(recaptchaUrl, {}, context);
        await isReady();
    };

    /** https://developers.google.com/recaptcha/docs/faq#id-like-to-hide-the-recaptcha-badge.-what-is-allowed */
    const hideBadge = () => {
        const style = context.document.createElement('style');
        style.textContent = '.grecaptcha-badge { visibility: hidden; }';

        context.document.head.append(style);
    };

    const isReady = async () =>
        new Promise<boolean>((resolve, reject): void => {
            const recaptcha = contextWithRecaptcha.grecaptcha?.enterprise;

            if (recaptcha == null) {
                reject(new Error('Recaptcha is not loaded.'));
            } else {
                recaptcha.ready(() => {
                    resolve(true);
                });
            }
        });

    /**
     * @param action - Please make sure that the action name matches the action name used by backend
     *     for the given endpoint, otherwise the recaptcha check will fail when you send
     *     the verification token this functions returns to our backend.
     *
     *     They are not linked in any way on purpose. If there's a mismatch,
     *     it indicates that an attacker is attempting to falsify the actions.
     *
     *     If we tried to link them automatically, for example by sending the action name in the request to our backend as a parameter,
     *     we'd make things easier for the attackers.
     *
     *     https://cloud.google.com/recaptcha-enterprise/docs/interpret-assessment-website#verify_action
     */
    const execute = async (action: RecaptchaAction) => {
        const recaptcha = contextWithRecaptcha.grecaptcha?.enterprise;

        if (apiToken == null) {
            return null;
        }

        if (recaptcha == null) {
            throw new Error('Recaptcha is not loaded.');
        }

        /*
         * Re-check isReady. We've seen cases where recaptcha fails with
         * "TypeError: Cannot read properties of null (reading 'then')",
         * This is an attempt to prevent that.
         */
        const ready = await isReady();

        if (!ready) {
            throw new Error('Recaptcha is not ready.');
        }

        return recaptcha.execute(apiToken, { action });
    };

    return {
        initialize,
        execute,
    };
};

export const isRecaptchaServiceInitialized = (contextWindow: ContextWithRecaptcha) =>
    contextWindow.grecaptcha?.enterprise != null;
